"use client";

import { usePrivy } from "@privy-io/react-auth";
import { Button } from "@/components/ui/button";
import { usePrivyAvailable } from "@/components/WalletConnectProvider";

interface PrivySignInButtonProps {
  /** Button label. Defaults to the combined email + wallet phrasing. */
  label?: string;
  /** Optional helper text shown beneath the button. */
  caption?: string;
}

/**
 * Primary auth CTA: opens Privy's login modal, which offers the only two
 * sanctioned account methods — an email one-time code, or connecting an
 * external wallet (MetaMask). Used as the default path on both the
 * sign-in and sign-up pages; email/password is a demoted backup behind a
 * disclosure on those pages.
 *
 * When the PrivyProvider isn't mounted (origin is plain HTTP and not
 * localhost, or NEXT_PUBLIC_PRIVY_APP_ID isn't set), the button renders in a
 * disabled "not configured" state rather than silently disappearing, so
 * operators can tell at a glance whether Privy is configured.
 *
 * The PrivyNextAuthBridge handles converting the resulting Privy session into
 * a NextAuth session.
 */
export function PrivySignInButton({
  label = "Continue with email or wallet",
  caption,
}: PrivySignInButtonProps) {
  const privyAvailable = usePrivyAvailable();
  return privyAvailable ? (
    <PrivySignInButtonInner label={label} caption={caption} />
  ) : (
    <PrivyUnavailableButton label={label} />
  );
}

function PrivyUnavailableButton({ label }: { label: string }) {
  const appIdMissing = !process.env.NEXT_PUBLIC_PRIVY_APP_ID;
  const reason = appIdMissing
    ? "Email and wallet sign-in are not configured in this environment."
    : "Email and wallet sign-in require HTTPS.";

  return (
    <div className="space-y-2">
      <Button type="button" disabled className="w-full">
        {label}
      </Button>
      <p className="text-xs text-gray-500 text-center">{reason}</p>
    </div>
  );
}

function PrivySignInButtonInner({
  label,
  caption,
}: {
  label: string;
  caption?: string;
}) {
  const { login, authenticated, ready } = usePrivy();

  return (
    <div className="space-y-2">
      <Button
        type="button"
        onClick={() => login()}
        disabled={!ready || authenticated}
        className="w-full"
      >
        {authenticated ? "Signing you in…" : label}
      </Button>
      {caption && (
        <p className="text-xs text-gray-500 text-center">{caption}</p>
      )}
    </div>
  );
}
